<template>
  <span
    ref="referenceEl"
    v-bind="$attrs"
    class="tooltip-container"
    :class="{ 'tooltip-container--auto-ellipsis': autoEllipsis }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <div v-if="tooltipState.renderDirectContent" ref="textElement" class="nowrap" :class="{ ellipsis: autoEllipsis }">
      {{ tooltipState.displayContent }}
    </div>

    <slot v-else />
  </span>

  <Teleport v-if="props.teleportToBody" to="body">
    <transition name="_transition" mode="in-out" @after-enter="onAfterEnter">
      <TooltipContent
        :id="id"
        ref="tooltipContentTeleported"
        :is-active="isActive"
        :should-show="!!tooltipState.shouldShow"
        :floating-styles="floatingStyles"
        :placement-class="placementClass || undefined"
        :theme-class="props.themeClass || undefined"
        :arrow="props.arrow"
        :arrow-styles="arrowStyles"
      >
        <slot name="content">{{ props.content }}</slot>
      </TooltipContent>
    </transition>
  </Teleport>

  <transition v-else name="_transition" mode="in-out" @after-enter="onAfterEnter">
    <TooltipContent
      :id="id"
      ref="tooltipContentInline"
      :is-active="isActive"
      :should-show="!!tooltipState.shouldShow"
      :floating-styles="floatingStyles"
      :placement-class="placementClass || undefined"
      :theme-class="props.themeClass || undefined"
      :arrow="props.arrow"
      :arrow-styles="arrowStyles"
    >
      <slot name="content">{{ props.content }}</slot>
    </TooltipContent>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useSlots, type CSSProperties } from 'vue'
import type { PropType } from 'vue'
import { onClickOutside, useWindowSize } from '@vueuse/core'
import { useFloating, flip, offset, shift, size, arrow as arrowMiddleware, type Placement, type Middleware } from '@floating-ui/vue'
import { useId } from '@/composables'
import { shortenText } from '@/services/utils'
import TooltipContent from './TooltipContent.vue'

const { width } = useWindowSize()

const props = defineProps({
  placement: { type: String as PropType<Placement>, default: 'top' },
  content: { type: String, default: undefined },
  maxWidth: { type: Number, default: 280 },
  customWidth: { type: Number, default: undefined },
  hover: { type: Boolean, default: true },
  textLength: { type: Number, default: 30 },
  autoEllipsis: { type: Boolean, default: false },
  arrow: { type: Boolean, default: false },
  teleportToBody: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  themeClass: { type: String, default: '' }
})

const emit = defineEmits<{ (e: 'update:active', value: boolean): void }>()
const slots = useSlots()

const isActive = ref(false)
const referenceEl = ref<HTMLElement | null>(null)
const textElement = ref<HTMLElement | null>(null)
const tooltipContentTeleported = ref<InstanceType<typeof TooltipContent> | null>(null)
const tooltipContentInline = ref<InstanceType<typeof TooltipContent> | null>(null)
const id = useId('tooltip')

const floatingEl = computed(() => {
  const activeContent = props.teleportToBody ? tooltipContentTeleported.value : tooltipContentInline.value
  return activeContent?.floatingEl || null
})

const arrowEl = computed(() => {
  const activeContent = props.teleportToBody ? tooltipContentTeleported.value : tooltipContentInline.value
  return activeContent?.arrowEl || null
})

const shouldRenderContent = computed(() => props.content !== undefined && props.content !== null && props.content !== '' && (!slots.default || slots.default().length === 0))

const getContent = computed(() => {
  const content = props.content || ''
  return content.length > props.textLength && !props.autoEllipsis ? shortenText(content, props.textLength) : content
})

const shouldRenderTooltipContent = computed(() => {
  if (props.disabled) return false

  if (props.autoEllipsis) {
    if (slots.default && slots.default().length > 0) {
      return !!(props.content || (slots.content && slots.content().length > 0))
    }
    if (textElement.value) {
      width.value
      return textElement.value.scrollWidth > textElement.value.clientWidth
    }
    return false
  } else {
    const content = props.content || ''
    if (slots.default && slots.default().length > 0) {
      return content.length > 0 || (slots.content && slots.content().length > 0)
    } else {
      return content.length > props.textLength
    }
  }
})

const tooltipState = computed(() => ({
  shouldShow: shouldRenderTooltipContent.value,
  renderDirectContent: shouldRenderContent.value,
  displayContent: getContent.value,
  hasContentSlot: slots.content && slots.content().length > 0
}))

async function activate() {
  // For autoEllipsis, do a fresh check on activation
  if (props.autoEllipsis && referenceEl.value && slots.default && slots.default().length > 0) {
    const firstChild = referenceEl.value.children[0] as HTMLElement
    if (firstChild) {
      // Check both horizontal and vertical overflow (for line-clamp based truncation)
      const hasOverflow = firstChild.scrollWidth > firstChild.clientWidth || firstChild.scrollHeight > firstChild.clientHeight
      if (!hasOverflow) {
        return // Don't activate if no overflow
      }
    }
  }

  if (isActive.value || !tooltipState.value.shouldShow) return
  isActive.value = true
  emit('update:active', true)

  if (floatingEl.value && referenceEl.value) {
    update()
  }
}

function deactivate() {
  if (!isActive.value) return
  isActive.value = false
  emit('update:active', false)
}

function handleMouseEnter() {
  if (!props.hover) return
  activate()
}

function handleMouseLeave() {
  if (!props.hover) return
  deactivate()
}

function handleClick(e: MouseEvent) {
  if (!props.hover) {
    onClick(e)
  }
}

function onClick(e: MouseEvent) {
  if ((e.target as HTMLElement | null)?.tagName?.toLowerCase() === 'a') return
  isActive.value ? deactivate() : activate()
}

const middleware = computed<Middleware[]>(() => {
  const result = [
    offset({
      mainAxis: 8,
      crossAxis: 0
    }),
    shift({
      crossAxis: true,
      padding: 8
    }),
    size({
      apply({ availableWidth, elements }) {
        Object.assign(elements.floating.style, {
          maxWidth: `${Math.min(availableWidth, props.maxWidth)}px`
        })
      }
    }),
    flip()
  ]

  if (props.arrow) {
    result.unshift(
      arrowMiddleware({
        element: arrowEl,
        padding: 0
      })
    )
  }

  return result
})

const {
  x,
  y,
  strategy,
  placement: currentPlacement,
  middlewareData,
  update
} = useFloating(referenceEl, floatingEl, {
  placement: computed(() => props.placement),
  middleware
})

const placementClass = computed(() => (currentPlacement.value ? `_placement-${currentPlacement.value.split('-')[0]}` : undefined))

const arrowStyles = computed((): CSSProperties => {
  if (!props.arrow) return { display: 'none' }
  return {
    position: 'absolute',
    left: middlewareData.value.arrow?.x ? `${middlewareData.value.arrow.x}px` : undefined,
    top: middlewareData.value.arrow?.y ? `${middlewareData.value.arrow.y}px` : undefined
  }
})

const floatingStyles = computed<CSSProperties>(() => {
  if (!isActive.value) return {}
  return {
    position: strategy.value,
    top: '0',
    left: '0',
    transform: `translate3d(${Math.round(x.value || 0)}px, ${Math.round(y.value || 0)}px, 0)`,
    pointerEvents: 'none',
    zIndex: 1000000,
    maxWidth: props.customWidth ? `${props.customWidth}px` : undefined
  }
})

function onAfterEnter() {
  update()
}

onMounted(() => {
  onClickOutside(referenceEl, (event) => {
    if (props.hover) return
    const floatingElement = floatingEl.value
    if (floatingElement && (floatingElement.contains(event.target as Node) || referenceEl.value?.contains(event.target as Node))) {
      return
    }
    if (isActive.value) deactivate()
  })
})
</script>

<style lang="scss">
.tooltip-container {
  display: inline-flex;
  max-width: 100%;

  &--auto-ellipsis {
    display: grid;
    grid-template-columns: 100%;
    overflow: hidden;
    min-width: 0;
  }
}
.trv-tooltip {
  background-color: $white;
  color: $bodyText;
  border-radius: 6px;
  box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
  padding: 8px 10px;

  font-family: 'Nunito';
  font-size: 14px;
  font-weight: normal;
  line-height: 18px;
  word-break: break-word;
  box-sizing: border-box;

  .trv-tooltip__content {
    pointer-events: none;
  }

  &__arrow {
    position: absolute;
    width: 12px;
    height: 12px;

    &::before {
      content: '';
      position: absolute;
      border-style: solid;
    }
  }

  // Transitions
  &._transition-enter-from,
  &._transition-leave-to {
    opacity: 0;
  }

  &._transition-enter-active,
  &._transition-leave-active {
    transition: opacity 0.2s ease-in-out;
  }

  // Arrow placements
  &._placement-left &__arrow {
    left: 100%;
    &::before {
      border-width: 6px 0 6px 6px;
      border-color: transparent transparent transparent $white;
    }
  }

  &._placement-right &__arrow {
    right: 100%;
    &::before {
      border-width: 6px 6px 6px 0;
      border-color: transparent $white transparent transparent;
    }
  }

  &._placement-top &__arrow {
    top: 100%;
    &::before {
      border-width: 6px 6px 0 6px;
      border-color: $white transparent transparent transparent;
    }
  }

  &._placement-bottom &__arrow {
    bottom: 100%;
    &::before {
      border-width: 0 6px 6px 6px;
      border-color: transparent transparent $white transparent;
    }
  }
}

.trv-tooltip.tooltip-rebranding {
  background-color: $rebranding-grey-900;
  color: $rebranding-grey-white;
  font-family: 'FT Sterling', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.nowrap {
  white-space: nowrap;
}

.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tooltip-trigger-content {
  display: inline-block;
  cursor: pointer;
}
</style>
